/**
 * The route the visitor was on at a moment of the page's life, for filing an
 * interaction metric against the page it happened on.
 *
 * An Event Timing entry's `startTime` is the dispatched event's `timeStamp`
 * (Event Timing spec), so sampling `currentPath()` as each interaction
 * event is dispatched resolves an entry to the route it happened on — no
 * router coupling, no patched `history.pushState`. Only changes are stored,
 * so the history grows per route interacted on, not per interaction.
 */
import { currentPath } from "./current-path";

/** Where every interaction INP can report is dispatched; a `keypress` shares its `keydown`'s `timeStamp`. */
export const INTERACTION_EVENT_TYPES = [
  "pointerdown",
  "pointerup",
  "click",
  "keydown",
  "keyup",
] as const;

export interface RouteHistory {
  /** The route when the history was created — the page-load route. */
  readonly initialPath: string | undefined;
  /** Note the route an event dispatched at `at` (performance timeline) saw. */
  record(at: number, path: string | undefined): void;
  /**
   * The route at `time` on the performance timeline. A time before the first
   * recorded interaction resolves to `initialPath`: nothing can have moved
   * the visitor off the load route without a later record saying so.
   */
  pathAt(time: number): string | undefined;
}

interface RouteChange {
  at: number;
  path: string | undefined;
}

export function createRouteHistory(
  initialPath: string | undefined,
): RouteHistory {
  const changes: RouteChange[] = [];
  let latestPath = initialPath;
  return {
    initialPath,
    record(at, path) {
      if (path === latestPath) return;
      latestPath = path;
      changes.push({ at, path });
    },
    pathAt(time) {
      for (let i = changes.length - 1; i >= 0; i--) {
        const change = changes[i];
        if (change !== undefined && change.at <= time) return change.path;
      }
      return initialPath;
    },
  };
}

export type InteractionTarget = Pick<
  EventTarget,
  "addEventListener" | "removeEventListener"
>;

/**
 * Record the route each interaction event is dispatched on. Returns the
 * uninstaller.
 */
// Capture so a handler's stopPropagation cannot hide the interaction, and so
// the route is read before that handler can navigate away from it; passive so
// a pointer or key event is never delayed by this.
export function trackInteractionRoutes(
  target: InteractionTarget,
  routes: RouteHistory,
  readPath: () => string | undefined = currentPath,
): () => void {
  const onInteraction = (event: Event) => {
    routes.record(event.timeStamp, readPath());
  };
  const options = { capture: true, passive: true } as const;
  for (const type of INTERACTION_EVENT_TYPES) {
    target.addEventListener(type, onInteraction, options);
  }
  return () => {
    for (const type of INTERACTION_EVENT_TYPES) {
      target.removeEventListener(type, onInteraction, options);
    }
  };
}
